import { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  BACKGROUNDS,
  Background,
  PRESET_ONLY_BACKGROUNDS,
  ThemeIds,
  resolveTheme,
} from '@porter/shared-theme';
import { LINE, LINE_MID, resetButton, ring } from './constants';

interface Props {
  /** Basis for the card previews — always a full theme. */
  theme: ThemeIds;
  /** Null when the shop has selected nothing yet. */
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function BackgroundCard({
  background,
  theme,
  selected,
  fromPreset,
  onSelect,
}: {
  background: Background;
  theme: ThemeIds;
  selected: boolean;
  fromPreset: boolean;
  onSelect: (id: string) => void;
}) {
  const t = resolveTheme({ ...theme, backgroundId: background.id });

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSelect(background.id)}
      aria-pressed={selected}
      sx={{
        ...resetButton,
        position: 'relative',
        border: `1px solid ${LINE_MID}`,
        borderRadius: '8px',
        overflow: 'hidden',
        background: '#fff',
        '&::after': ring(selected),
      }}>
      <Box
        sx={{
          height: 104,
          padding: '10px',
          background: background.css,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}>
        <Box sx={{ height: 26, background: 'rgba(150, 150, 150, 0.5)' }} />
        <Box
          sx={{
            fontFamily: t.headFont,
            fontSize: 11,
            fontWeight: 700,
            color: t.fg,
            lineHeight: 1,
          }}>
          Porter Studio
        </Box>
        <Box sx={{ height: 3, width: '55%', background: t.dim }} />
        <Box
          sx={{
            marginTop: 'auto',
            height: 13,
            boxSizing: 'border-box',
            borderRadius: '3px',
            border: t.primaryEdge,
            background: t.primary,
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          padding: '8px 10px',
          borderTop: `1px solid ${LINE}`,
        }}>
        <Box sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary' }}>
          {background.name}
        </Box>
        <Box
          sx={{
            fontSize: 10,
            fontWeight: 600,
            color: fromPreset ? 'primary.main' : 'text.tertiary',
            flex: 'none',
          }}>
          {fromPreset ? 'From preset' : background.mode}
        </Box>
      </Box>
    </Box>
  );
}

function BackgroundPicker({ theme, selectedId, onSelect }: Props) {
  // A preset can select a background the picker doesn't list — surface it.
  const options = useMemo(() => {
    if (!selectedId || BACKGROUNDS.some(bg => bg.id === selectedId))
      return BACKGROUNDS;
    const fromPreset = PRESET_ONLY_BACKGROUNDS.find(bg => bg.id === selectedId);
    return fromPreset ? [fromPreset, ...BACKGROUNDS] : BACKGROUNDS;
  }, [selectedId]);

  return (
    <>
      <Box sx={{ margin: '0 0 14px', fontSize: 14, color: 'text.secondary' }}>
        Fills the whole booking site. Dark backgrounds flip your text to light
        automatically.
      </Box>
      <Box
        sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        {options.map(background => (
          <BackgroundCard
            key={background.id}
            background={background}
            theme={theme}
            selected={background.id === selectedId}
            fromPreset={!BACKGROUNDS.some(bg => bg.id === background.id)}
            onSelect={onSelect}
          />
        ))}
      </Box>
    </>
  );
}

export default BackgroundPicker;
